import $ from 'wee-dom';
import $events from 'wee-events';
import $mediator from 'wee-mediator';
import { _html, _doc } from 'core/variables';
import Tabbable from '../../../scripts/tabbable';
import KeyHelper from '../../../scripts/keyhelper';

export default class MobileNavigation {
    constructor() {
        /**
         * The parent for the panels, we use this for adding scope
         * to our selections
         * @member {string}
         */
        this.parent = ':mobileNavigationPanels';

        /**
         * The site header
         * @member {$}
         */
        this.$siteHeader = $(':siteHeaderMain');

        /**
         * The main navigation element
         * @member {$}
         */
        this.$el = $(':mobileNavigation');

        /**
         * The wrapping div for the two menu sections
         * @member {$}
         */
        this.$navigationSections = $(':mobileNavigationSections');

        /**
         * The button to open the mobile navigation
         * @member {$}
         */
        this.$button = $(':mobileNavigationToggle');

        /**
         * Navigation links
         * @member {$}
         */
        this.$links = $(':mobileNavigationLink');

        /**
         * Wee selection of the HTML element
         * @member {$}
         */
        this.$html = $(_html);

        /**
         * Mobile nav sub menu links
         * @member {$}
         */
        this.$subMenuLinks = $(':mobileNavigationSubMenuLink');

        /**
         * Back button returns user to normal menu state
         * @member {$}
         */
        this.$backButton = $(':mobileNavigationBackButton');

        /**
         * Nav option panels
         * @member {$}
         */
        this.$panels = $(':navigationPanel', this.parent);

        /**
         * The main navigation panel
         * @member {$}
         */
        this.$mainPanel = $(':mobileNavigationMenuSection');

        /**
         * The panel navigation panel
         * @member {$}
         */
        this.$panelPanel = $(':mobileNavigationPanelSection');

        /**
         * Bottom action links
         * @member {$}
         */
        this.$bottomActions = $(':navigationPanelBottomAction', this.parent);

        /**
         * The internal open state
         * @member {boolean}
         */
        this.isOpen = false;

        /**
         * The internal sliding state
         * @member {boolean}
         */
        this.isSlid = false;

        /**
         * The internal reference to the tabbable instance
         * @member {null|Tabbable}
         */
        this.tabbable = null;
    }

    /**
     * Open the navigation
     *
     * @return {this}
     */
    open() {
        this.operate();

        return this;
    }

    /**
     * Close the navigation
     *
     * @return {this}
     */
    close() {
        this.operate(false);

        return this;
    }

    /**
     * Toggle the navigation
     *
     * @return {this}
     */
    toggle() {
        this.operate(! this.isOpen);

        return this;
    }

    /**
     * Trigger the navigation transition slide and toggle the requested option panel
     *
     * @param {string} panel - The name of the panel as a data attribute on the element
     */
    slideTo(panel) {
        const $panels = this.$panels;

        // Set internal slide state
        this.isSlid = true;

        // Grab the current tabbed item for use later
        this.lastTabbed = _doc.activeElement;

        // Slide the container
        this.$navigationSections.activate();

        // Disable all panels
        $panels.each((panel) => $(panel).disable());

        // Disable all bottom action button tabbing
        this.$bottomActions.tabindex('-1');

        // Add proper aria hidden attribute to menu sections
        this.$panelPanel.ariaHidden(false);
        this.$mainPanel.ariaHidden(true);

        // Loop over options and enable the selected option
        for (let i = 0; i < $panels.length; i += 1) {
            const $option = $($panels[i]);

            if ($option.data('type') === panel) {
                this.activePanel = panel;

                $option.enable()
                    .ariaHidden(false)
                    // Enable bottom action button tabbing
                    .find(this.$bottomActions.sel)
                    .tabindex('0');

                // Create a new instance of the tabbable
                // class to trap the tab focus
                this.tabbable = new Tabbable({
                    el: $option,
                    prepend: [
                        this.$backButton[0],
                    ],
                    callback: () => {
                        this.resetFocus();
                    },
                });

                $events.on({
                    [this.$button.sel]: {
                        keydown: (e) => {
                            if (KeyHelper.isTab(e) && this.tabbable.$tabbableEls.length) {
                                this.tabbable.$tabbableEls.eq(0)[0].focus();

                                e.preventDefault();
                            }
                        },
                    },
                }, {
                    namespace: 'navigationMobile',
                });

                break;
            }
        }

        // Put the back button back in the tab order
        this.$backButton.tabindex('0');

        // Pull the links and sublinks out of the tab order
        this.$links.tabindex('-1');
        this.$subMenuLinks.tabindex('-1');
    }

    closePanel() {
        this.$el.deactivate();
        this.$navigationSections.ariaHidden(true);
        this.$button.ariaExpand(false);
        this.isOpen = false;
    }

    /**
     * Operate the navigation
     *
     * @param {boolean} state - The current 'open' state of the navigation
     */
    operate(state = true) {
        this.$el[state ? 'activate' : 'deactivate']();
        this.$html[state ? 'disable' : 'enable']();
        this.$siteHeader[state ? 'activate' : 'deactivate']();
        this.$navigationSections.ariaHidden(! state);
        this.$button.ariaExpand(state);
        this.isOpen = state;

        if (state) {
            this.tabbable = new Tabbable({
                el: this.$mainPanel,
                callback: () => this.resetFocus(),
            });
        } else {
            this.tabbable = null;
        }

        if (this.isSlid) {
            this.slideBack();
        }

        $mediator.emit('navigation.mobile.operate', state);
    }

    /**
     * Slide the navigation panel back to it's original position
     */
    slideBack() {
        // Reset slid state
        this.isSlid = false;

        // Put menu links back in tab index
        this.$links.tabindex('0');
        this.$subMenuLinks.tabindex('0');

        // Reset focus to last tabbed menu item
        this.lastTabbed.focus();

        // Return container to original position
        this.$navigationSections.deactivate();

        // Pull back button out of the tab order
        this.$backButton.tabindex('-1');

        // Put first bottom action back in tab order
        this.$bottomActions.eq(0).tabindex('0');

        // Add proper aria hidden attribute to hidden panel
        this.$panelPanel.ariaHidden();
        this.$mainPanel.ariaHidden(false);

        if (this.tabbable) {
            this.tabbable.destroy();
            this.tabbable = null;
        }

        $events.off(null, '.navigationMobile');
    }

    /**
     * Manually set focus on the back button
     */
    focusBackButton() {
        if (this.isSlid) {
            this.$backButton[0].focus();
        }
    }

    /**
     * Resets focus on the nav toggle button
     */
    resetFocus() {
        this.$button[0].focus();
    }

    /**
     * Unbind events and reset parameters
     */
    destroy() {
        this.close();

        this.tabbable = null;
        this.isOpen = false;
        this.isSlide = false;
        $events.off(null, '.navigationMobile');
    }
}
